import type { Metadata } from 'next';

export const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://buildersitenow.com';

export interface MarketingMetadataInput {
  /** Page path starting with "/" — used for canonical and OG URL. */
  path: string;
  title: string;
  description: string;
  keywords?: string[];
  /** Set to true on private/auth surfaces (login, plan-expired, status). */
  noIndex?: boolean;
  /**
   * The OTHER language's version of this page, as a path.
   *
   * On an English page that is the Tamil path (`/ta/modules/sites`); on a
   * Tamil page it is the English path (`/modules/sites`). It was called
   * `taPath` and that reading — "the Tamil path, always" — is wrong on half
   * the pages that use it, which is a silent hreflang bug rather than a
   * visible one. Named for what it is instead.
   *
   * Emits reciprocal hreflang (`en-IN`, `ta-IN`, `x-default`) so Google serves
   * the right language instead of treating the two as duplicates. Pass it on
   * BOTH pages — hreflang that is not mutual is ignored.
   */
  altPath?: string;
  /** True when THIS page is the Tamil one; flips which side of the pair it is. */
  isTamil?: boolean;
}

/**
 * Generates consistent SEO metadata for every public marketing page:
 *  - canonical URL (search-engine-friendly)
 *  - OpenGraph card (LinkedIn / WhatsApp / Slack previews)
 *  - Twitter summary_large_image card (X previews)
 *  - en_IN locale, BuilderSiteNow siteName, web type
 *
 * Per-page metadata can spread the result and override any field.
 */
export function marketingMetadata({
  path, title, description, keywords, noIndex, altPath, isTamil,
}: MarketingMetadataInput): Metadata {
  const url = `${SITE_URL}${path}`;
  // An unpaired page emits no `languages` block at all: a self-referencing
  // hreflang with no counterpart tells Google nothing and risks it treating
  // the pair as duplicates rather than alternates.
  // x-default points at the English page in both directions: it is the
  // fallback for a searcher whose language we do not match, and English is
  // the wider net of the two.
  const enUrl = isTamil ? `${SITE_URL}${altPath}` : url;
  const languages = altPath
    ? { 'en-IN': enUrl, 'ta-IN': isTamil ? url : `${SITE_URL}${altPath}`, 'x-default': enUrl }
    : undefined;
  return {
    title,
    description,
    keywords,
    alternates: { canonical: url, ...(languages ? { languages } : {}) },
    robots: noIndex
      ? { index: false, follow: false }
      : { index: true, follow: true, googleBot: { index: true, follow: true, 'max-image-preview': 'large', 'max-snippet': -1 } },
    openGraph: {
      type: 'website',
      siteName: 'BuilderSiteNow',
      title,
      description,
      url,
      locale: isTamil ? 'ta_IN' : 'en_IN',
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
    },
  };
}

/**
 * BreadcrumbList JSON-LD — drop into a marketing page so Google can render
 * the breadcrumb trail in search results. Pass the visible chain ending in
 * the current page.
 */
export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((i, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: i.name,
      item: `${SITE_URL}${i.path}`,
    })),
  };
}

/**
 * FAQPage JSON-LD — drop into any marketing page that renders a visible FAQ.
 * Google requires the answer text to match what's on the page, so build both
 * the markup and this schema from the same array.
 */
export function faqJsonLd(items: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((i) => ({
      '@type': 'Question',
      name: i.q,
      acceptedAnswer: { '@type': 'Answer', text: i.a },
    })),
  };
}

/**
 * Public social / directory profiles, for Organization.sameAs.
 *
 * Google uses these to consolidate the brand into ONE entity; an empty array
 * means it has nothing to join on and a knowledge panel cannot form. Every
 * entry must be a profile that genuinely exists and genuinely belongs to us —
 * a guessed or aspirational URL is worse than an empty list, because it points
 * the entity at someone else.
 *
 * Stored canonical and bare: no `fbclid`/`utm_*` query junk, no trailing '#'.
 * A tracking parameter in a sameAs URL is a different URL to Google, and a
 * profile it cannot match is a profile that does not count.
 *
 * TODO(owner): LinkedIn is the one still missing, and for software sold to
 * business owners it is the most useful of the four. Add the company page here
 * once it exists. The Facebook entry is also a numeric profile.php id rather
 * than a vanity URL — claiming facebook.com/buildersitenow on the page itself
 * and swapping it in here would read as a stronger brand signal, but the
 * numeric URL is genuine and resolves, so it belongs here meanwhile.
 */
export const SOCIAL_PROFILES: string[] = [
  'https://www.facebook.com/profile.php?id=61592161543037',
  'https://www.instagram.com/buildersite_now/',
  'https://www.youtube.com/@BuilderSiteNow',
];

/**
 * The registered business contact — phone and postal address.
 *
 * ── Why every field starts empty ──────────────────────────────────────────
 * A fabricated NAP (name/address/phone) is actively worse for local SEO than
 * no NAP, because Google cross-checks it against the Google Business Profile,
 * directory listings and the MCA register, and a conflict teaches it that the
 * business cannot be trusted on its own facts. So nothing here is invented and
 * nothing is rendered until these strings are filled in.
 *
 * ── Why the scaffolding exists anyway ─────────────────────────────────────
 * The missing phone number is not a cosmetic gap. The buyer is a Tamil Nadu
 * SMB builder who calls before filling in a form, and with no postal address
 * there is no `LocalBusiness` node, which means the site is not eligible for
 * the local pack — the map results where someone searching "construction
 * software Coimbatore" actually looks. Having the wiring ready means turning
 * both on is a single edit to this object rather than a change across the
 * schema, the footer and the contact page.
 *
 * TODO(owner): fill these in. `phone` must be in E.164 (+91XXXXXXXXXX) for
 * schema.org; `phoneDisplay` is what humans see and can be formatted freely.
 * Leaving a field '' keeps that half switched off — a phone with no address
 * is valid and still worth publishing on its own.
 */
export const BUSINESS_CONTACT = {
  /** E.164, e.g. '+919876543210'. Empty = no phone published anywhere. */
  phone: '+917845183709',
  /** Human-readable, e.g. '+91 98765 43210'. Falls back to `phone`. */
  phoneDisplay: '+91 78451 83709',
  /** Street and building, e.g. '12/4 Anna Salai, 2nd Floor'. */
  streetAddress: '',
  /** City, e.g. 'Chennai'. */
  addressLocality: '',
  /** State, e.g. 'Tamil Nadu'. */
  addressRegion: '',
  /** PIN code, e.g. '600002'. */
  postalCode: '',
  /**
   * The number that answers WhatsApp, digits only with country code
   * ('919876543210'). Separate from `phone` on purpose: it is often a
   * different handset from the landline, and it is the one channel this buyer
   * actually uses — a Tamil Nadu builder will message before they ring and
   * ring before they type into a form.
   *
   * Unlike the postal address this needs NO Google Business Profile, no
   * verification and no schema to be useful: one number here and every page
   * gains a one-tap path to a human. It is the cheapest conversion fix
   * available to this site and the only reason it is dark is that nobody has
   * supplied the number.
   */
  whatsapp: '917845183709',
  /**
   * Opening hours in schema.org's compact form, e.g.
   * ['Mo-Fr 09:30-18:30', 'Sa 09:30-13:30']. Empty = omitted.
   */
  openingHours: ['Mo-Sa 10:00-19:00'] as string[],
} as const;

/** True once there is a real postal address to publish. */
export const hasBusinessAddress = (): boolean => Boolean(
  BUSINESS_CONTACT.streetAddress
  && BUSINESS_CONTACT.addressLocality
  && BUSINESS_CONTACT.addressRegion
  && BUSINESS_CONTACT.postalCode,
);

/** True once there is a real phone number to publish. */
export const hasBusinessPhone = (): boolean => Boolean(BUSINESS_CONTACT.phone);

/** What a human should see for the phone — display form, else the E.164 one. */
export const businessPhoneDisplay = (): string =>
  BUSINESS_CONTACT.phoneDisplay || BUSINESS_CONTACT.phone;

/** True once there is a WhatsApp number to publish. */
export const hasBusinessWhatsApp = (): boolean => Boolean(BUSINESS_CONTACT.whatsapp);

/**
 * A click-to-chat link, optionally pre-filling the first message.
 *
 * `wa.me` rather than `api.whatsapp.com`: it opens the installed app on a
 * phone and WhatsApp Web on a desktop without us having to detect which. The
 * prefilled text is a courtesy — it saves the reader composing an opener, and
 * it tells us which page they were on when they decided to message.
 *
 * Returns '' when no number is configured, so every call site can render
 * conditionally on the same value it would link to.
 */
export const whatsAppHref = (message?: string): string => {
  const digits = BUSINESS_CONTACT.whatsapp.replace(/\D/g, '');
  if (!digits) return '';
  const q = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${digits}${q}`;
};

/**
 * The districts the business actually sells into, as schema.org places.
 *
 * Factual, not aspirational: these are the Tamil Nadu districts named as the
 * target market in the go-to-market docs, and the ones the city landing pages
 * cover. `areaServed` is how a national-scope Organization tells Google where
 * it operates without pretending to have an office in each one.
 */
export const SERVICE_AREAS = [
  'Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem',
  'Erode', 'Tiruppur', 'Chengalpattu', 'Kancheepuram', 'Tiruvallur',
];

/**
 * Organization schema — emitted on every marketing page (see MarketingShell)
 * so Google can build a consistent brand entity (knowledge-panel candidate).
 * Logo references the static `/icon` PNG generated by `src/app/icon.tsx`.
 */
export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}#organization`,
  name: 'BuilderSiteNow',
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/icon`,
    width: 512,
    height: 512,
  },
  description: 'Construction ERP for Indian builders. Manage sites, share status with clients, track vendors, bills, employees and ledger in one calm place.',
  // Country first, then the districts actually sold into. No street address or
  // telephone until there is a real one to publish — a fabricated NAP is worse
  // for local SEO than none, because it conflicts with every other source.
  areaServed: [
    { '@type': 'Country', name: 'India' },
    ...SERVICE_AREAS.map((name) => ({ '@type': 'AdministrativeArea', name })),
  ],
  contactPoint: [{
    '@type': 'ContactPoint',
    contactType: 'sales',
    email: 'support@buildersitenow.com',
    // A phone on the ContactPoint is what lets Google show a call button
    // against the brand. Spread rather than set, so the key is absent — not
    // present-and-empty — until there is a number.
    ...(hasBusinessPhone() ? { telephone: BUSINESS_CONTACT.phone } : {}),
    areaServed: 'IN',
    availableLanguage: ['en', 'ta'],
  }],
  ...(hasBusinessPhone() ? { telephone: BUSINESS_CONTACT.phone } : {}),
  ...(hasBusinessAddress() ? { address: postalAddressJsonLd() } : {}),
  // `sameAs: []` is not neutral — it asserts "this brand has no presence
  // anywhere else", which for a young domain is a trust signal pointing the
  // wrong way. Omit the key entirely until there is something true to put in
  // it, so Google treats it as unknown rather than as absent.
  ...(SOCIAL_PROFILES.length > 0 ? { sameAs: SOCIAL_PROFILES } : {}),
};

/** The postal address as schema.org wants it. Only call when `hasBusinessAddress()`. */
export function postalAddressJsonLd() {
  return {
    '@type': 'PostalAddress',
    streetAddress: BUSINESS_CONTACT.streetAddress,
    addressLocality: BUSINESS_CONTACT.addressLocality,
    addressRegion: BUSINESS_CONTACT.addressRegion,
    postalCode: BUSINESS_CONTACT.postalCode,
    addressCountry: 'IN',
  };
}

/**
 * LocalBusiness node — the thing that makes the site eligible for the local
 * pack (map results).
 *
 * Returns `null` until there is a real address, and the callers render nothing
 * in that case. `ProfessionalService` is used rather than bare `LocalBusiness`
 * because schema.org has no software-vendor subtype and a plain LocalBusiness
 * on a company that sells nationally reads as a mis-categorised storefront.
 *
 * `areaServed` repeats the district list deliberately: the office is in one
 * place, the service covers the state, and conflating the two is how local
 * schema ends up either too narrow to rank in Coimbatore or broad enough to
 * look like spam.
 */
export function localBusinessJsonLd() {
  if (!hasBusinessAddress()) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${SITE_URL}#localbusiness`,
    name: 'BuilderSiteNow',
    url: SITE_URL,
    image: `${SITE_URL}/icon`,
    parentOrganization: { '@id': `${SITE_URL}#organization` },
    address: postalAddressJsonLd(),
    ...(hasBusinessPhone() ? { telephone: BUSINESS_CONTACT.phone } : {}),
    email: 'support@buildersitenow.com',
    priceRange: '₹₹',
    areaServed: SERVICE_AREAS.map((name) => ({ '@type': 'AdministrativeArea', name })),
    availableLanguage: ['en', 'ta'],
    ...(BUSINESS_CONTACT.openingHours.length > 0
      ? { openingHours: [...BUSINESS_CONTACT.openingHours] }
      : {}),
  };
}

/**
 * WebSite schema — declares the brand to Google so it can render the
 * brand title and (eventually) sitelinks search. Includes a `SearchAction`
 * pointing at the future site-search endpoint; if `/search` is ever added
 * Google can surface a search box directly in results.
 */
export const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}#website`,
  url: SITE_URL,
  name: 'BuilderSiteNow',
  description: 'The construction ERP built for Indian builders.',
  publisher: { '@id': `${SITE_URL}#organization` },
  inLanguage: 'en-IN',
};
